import type { Locale } from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';
import { Box, Heading, Stack } from 'braid-design-system';
import { observer } from 'mobx-react';
import { useState } from 'react';

import { TRACKING_CANDIDATE_DETAILS } from 'src/constants/pageTrackingData';
import type { PageConfig } from 'src/libs/PageConfig';
import type { IAttachmentsStore } from 'src/stores/JobApplicationStore/AttachmentsStore';
import type { INotes } from 'src/stores/JobApplicationStore/Notes';
import { ACTION_DONE, ACTION_ERROR } from 'src/stores/state';
import { trackAddAttachmentStart } from 'src/utils/events';
import type { TrackStatusUpdateAttributes } from 'src/utils/eventsTypes';

import type {
  ApplicationAttachmentV2,
  ApplicationNoteV2,
} from '../../graphql/hirer-types.generated';

import translations from './.vocab';
import { AddNotesAttachments } from './AddNotesAttachment/AddNotesAttachment';
import { NotesAttachmentError } from './NotesAttachmentError/NotesAttachmentError';
import { useNotesAttachmentHistoryQuery } from './NotesAttachmentHistory.hirer.generated';
import { NotesAttachmentList } from './NotesAttachmentList/NotesAttachmentList';

import * as styles from '../NotesAttachments/Item/ItemContent/ItemContent.css';

interface NotesAttachmentsProps {
  notes: INotes;
  attachmentsStore: IAttachmentsStore;
  locale: Locale;
  isVerifiedId: TrackStatusUpdateAttributes['isVerifiedId'];
  jobId: string;
  applicationCorrelationId: string;
  addCustomError: PageConfig['dataDogRum']['addCustomError'];
  updateStoreWithNotesAttachmentData: (
    attachmentData: ApplicationAttachmentV2[],
    notesData: ApplicationNoteV2[],
  ) => void;
  userId: string;
  source: number;
}
const NotesAttachments = ({
  notes,
  attachmentsStore,
  locale,
  isVerifiedId,
  jobId,
  applicationCorrelationId,
  addCustomError,
  updateStoreWithNotesAttachmentData,
  userId,
  source,
}: NotesAttachmentsProps) => {
  const { t } = useTranslations(translations);

  // FIXME: This is a existing solution to show the error message for notes
  // This will be refactored in the follow up PR.
  const [showAddNoteActionError, setShowAddNoteActionError] = useState(false); // showNotesError

  const {
    loading,
    data: notesAttachmentsData,
    error: apolloError,
    refetch,
  } = useNotesAttachmentHistoryQuery({
    variables: {
      applicationNotesInput: {
        applicationCorrelationId,
        jobId,
      },
      applicationAttachmentsV2Input: {
        applicationCorrelationId,
        jobId,
      },
    },
    onCompleted: (fetchedData) => {
      const attachData =
        fetchedData.applicationAttachmentsV2.__typename ===
        'ApplicationAttachmentsV2ResponseSuccess'
          ? fetchedData.applicationAttachmentsV2.result
          : [];

      const noteData =
        fetchedData.applicationNotesV2.__typename ===
        'ApplicationNotesResponseSuccess'
          ? fetchedData.applicationNotesV2.result
          : [];

      updateStoreWithNotesAttachmentData(attachData, noteData);
    },
    fetchPolicy: 'cache-and-network',
  });

  const attachmentResponseError =
    notesAttachmentsData?.applicationAttachmentsV2.__typename ===
    'ResponseError';

  const notesResponseError =
    notesAttachmentsData?.applicationNotesV2.__typename === 'ResponseError';

  // FIXME: This will call graphql mutaion
  const onDeleteAttachment = async (
    attachmentItem: ApplicationAttachmentV2,
  ): ReturnType<typeof attachmentsStore.deleteAttachment> => {
    const deleteResponse = await attachmentsStore.deleteAttachment({
      legacyAttachmentId: attachmentItem.legacyAttachmentId ?? null,
      blobId: attachmentItem.blobId,
      addCustomError,
    });

    refetch();
    return deleteResponse;
  };

  const onDeleteNote = async (
    attachmentItem: ApplicationNoteV2,
  ): ReturnType<typeof notes.removeNote> => {
    const deleteResponse = await notes.removeNote(
      attachmentItem.id,
      addCustomError,
    );

    refetch();
    return deleteResponse;
  };

  // FIXME: This will call graphql mutaion
  const onAddNote = async (content: string) => {
    const addNoteResponse = await notes.addNote(content, addCustomError);

    if (addNoteResponse === ACTION_ERROR) {
      setShowAddNoteActionError(true);
      return;
    }
    if (addNoteResponse === ACTION_DONE) {
      refetch();
    }
    return;
  };

  const onAddAttachmentClick = () =>
    trackAddAttachmentStart({
      currentPage: TRACKING_CANDIDATE_DETAILS,
      applicationCorrelationId,
      applicationId: attachmentsStore.jobApplication.jobApplicationId,
      seekCandidateId: attachmentsStore.jobApplication.seekCandidateId,
      adcentreCandidateId: attachmentsStore.jobApplication.candidateId,
    });

  const addAttachmentAction = async (content: File) => {
    const attachmentResponse = await attachmentsStore.addAttachment({
      jobId,
      applicationCorrelationId,
      file: content,
      addCustomError,
    });
    if (attachmentResponse === ACTION_DONE) {
      refetch();
    }
    return attachmentResponse;
  };

  return (
    <Box marginBottom="medium" className={styles.notesAttachmentsContainer}>
      <Stack space="xlarge">
        <AddNotesAttachments
          showServerError={showAddNoteActionError}
          isLoading={loading}
          locale={locale}
          onAddNote={onAddNote}
          addAttachmentAction={addAttachmentAction}
          onAddAttachmentClick={onAddAttachmentClick}
          source={source}
        />

        <Stack space="xxsmall">
          <Box paddingBottom="xxsmall">
            <Heading level="4">{t('Notes and attachments history')}</Heading>
          </Box>
          <NotesAttachmentError
            error={apolloError}
            notesResponseError={notesResponseError}
            attachmentResponseError={attachmentResponseError}
          />
          <NotesAttachmentList
            notesAttachmentsData={notesAttachmentsData}
            locale={locale}
            isVerifiedId={isVerifiedId}
            jobId={jobId}
            applicationCorrelationId={applicationCorrelationId}
            onDeleteAttachment={onDeleteAttachment}
            onDeleteNote={onDeleteNote}
            userId={userId}
            jobApplicationId={attachmentsStore.jobApplication.jobApplicationId}
            seekCandidateId={attachmentsStore.jobApplication.seekCandidateId}
            candidateId={attachmentsStore.jobApplication.candidateId}
          />
        </Stack>
      </Stack>
    </Box>
  );
};

export default observer(NotesAttachments);
